import { 
  useEffect, 
  useRef, 
  useState 
} from 'react'

import { JitsiVideoConference } from '@components'
import { utcToMySql } from '@helpers'

import { 
  Modal, 
  ModalBody, 
  ModalContent, 
  ModalHeader 
} from '@heroui/react'

import { CalendarDaysIcon } from '@heroicons/react/24/outline'
import BookingDetailsPane from './BookingDetailsPane'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'

const VideoConferenceModal = ({ 
  bookingDetails, 
  event, 
  handleClose, 
  handleLogConferenceSummary,
  handleUpdateTeacherComment, 
  jwtToken, 
  roomId, 
  vcAutoDisconnect 
}) => {
  const jitsiApiRef = useRef(null)
  const eventPayloads = useRef([])
  const noOfParticipantsRef = useRef(0)

  const eventTimes = useRef({
    participantJoined: null,
    participantLeft: null,
    joinButtonClicked: null,
    conferenceJoined: null,
    conferenceEnded: null
  })

  const [isConferenceActive, setIsConferenceActive] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      const now = dayjs()

      if (vcAutoDisconnect && bookingDetails.ends_at.isBefore(now) && isConferenceActive) {
        handleCloseJitsi(true)
      }
    }, 1000); // Runs every second

    return () => clearInterval(interval);
  }, [roomId, isConferenceActive, vcAutoDisconnect, bookingDetails.ends_at]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseJitsi = (hangup = true) => {
    if (!eventTimes.current.conferenceEnded && eventTimes.current.conferenceJoined) {
      eventTimes.current.conferenceEnded = utcToMySql()

      // Fire-and-forget: does NOT block UI
      handleLogConferenceSummary(eventTimes.current, eventPayloads.current, noOfParticipantsRef.current)
    }

    if (jitsiApiRef?.current && hangup) {
      handleHangupJitsiCall()
      
      // Hacky way to save comments on auto disconnect
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
      
      // Delay closing to allow Jitsi to process hangup
      setTimeout(() => { handleClose() }, 100)
    } else {
      handleClose()
    }
  }

  const handleConferenceLeft = () => {
    handleCloseJitsi(false)
    setIsConferenceActive(false)
  }

  const handleConferenceJoined = () => {
    eventTimes.current.conferenceJoined = utcToMySql()
    setIsConferenceActive(true)
  }

  const handleParticipantJoined = (payload) => {
    const now = utcToMySql()
    noOfParticipantsRef.current += 1
    
    // record only the first participant joined time
    if (!eventTimes.current.participantJoined) {
      eventTimes.current.participantJoined = now
    }
    
    eventPayloads.current.push({
      type: 'participantJoined',
      payload,
      timestamp: now
    })
  }
  
  const handleParticipantLeft = (payload) => {
    const now = utcToMySql()

    // record only the last participant left time
    eventTimes.current.participantLeft = now

    eventPayloads.current.push({
      type: 'participantLeft',
      payload,
      timestamp: now
    })
  }

  const handleHangupJitsiCall = () => {
    jitsiApiRef.current.executeCommand("hangup")
  }
  
  const handleJoinButtonClicked = () => {
    eventTimes.current.joinButtonClicked = utcToMySql()
  }

  return (
    <Modal
      classNames={{
        base: "w-[95%] !max-h-[800px] sm:w-full sm:max-h-[80%] md:max-h-[90%] lg:max-h-[90%] h-full lg:max-w-[916px] xl:max-w-7xl bg-white",
        body: "p-0 rounded-b-lg",
        header: "bg-indigo-800 rounded-t-lg text-white",
        wrapper: "z-[100] p-4 md:items-center",
        closeButton: "hover:bg-white/5 text-white mt-2 mr-2",
      }}
      isDismissable={false}
      isKeyboardDismissDisabled={true}
      isOpen={true}
      onClose={() => handleCloseJitsi(true)}
      placement="center"
      radius="lg"
      scrollBehavior="inside"
      size="4xl"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex items-center justify-between">
              <div className='flex items-center'>
                <CalendarDaysIcon className="w-8 h-8 mr-2" />
                <p>{bookingDetails.starts_at.format('H:mm')} - {bookingDetails.ends_at.format('H:mm')} - {bookingDetails.parent}</p>
              </div>
            </ModalHeader>

            <ModalBody>
              <div className='flex flex-col-reverse h-full xl:flex-row'>
                {isConferenceActive && (
                  <BookingDetailsPane 
                    bookingDetails={bookingDetails} 
                    handleUpdateTeacherComment={(comment) => handleUpdateTeacherComment(comment)}
                    vcAutoDisconnect={vcAutoDisconnect} 
                  />
                )}
                
                <JitsiVideoConference
                  apiRef={jitsiApiRef}
                  bookingSlotId={bookingDetails.booking_slot_id}
                  event={event}
                  jwtToken={jwtToken}
                  roomId={roomId}
                  handleClose={handleCloseJitsi}
                  handleConferenceJoined={handleConferenceJoined}
                  handleConferenceLeft={handleConferenceLeft}
                  handleJoinButtonClicked={handleJoinButtonClicked}
                  handleParticipantJoined={handleParticipantJoined}
                  handleParticipantLeft={handleParticipantLeft}
                />
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

VideoConferenceModal.propTypes = {
  bookingDetails: PropTypes.shape({
    parent: PropTypes.string,
    student: PropTypes.string,
    lesson: PropTypes.string,
    subject: PropTypes.string,
    starts_at: PropTypes.object,
    ends_at: PropTypes.object
  }),
  event: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleUpdateTeacherComment: PropTypes.func.isRequired,
  jwtToken: PropTypes.string.isRequired,
  roomId: PropTypes.string.isRequired,
  vcAutoDisconnect: PropTypes.bool
}

export default VideoConferenceModal